/**
 * Midwood Soles — computed size mapper (theme only).
 * Reads option + SizeLocale + title + vendor.
 * Does not write variants, SKUs, SizeLocale, or metafields.
 * Port of /workspace/midwood/size-audit/mapping-plan.md §2 + §5.4
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.MidwoodSizeMapper = factory();
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  function fmtNum(n) {
    if (n == null || isNaN(n)) return "";
    if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
    return String(n);
  }

  function norm(s) {
    return String(s || "").trim().replace(/\s+/g, " ");
  }

  function isApparel(lower) {
    return /^(xs|s|m|l|xl|xxl|xxxl|2xl|3xl|os|one size)$/.test(lower);
  }

  function parseHalfJoin(token) {
    var m = String(token).match(/^(\d+)(?:-(5))?$/);
    if (m) {
      var a = parseInt(m[1], 10);
      return m[2] === "5" ? a + 0.5 : a;
    }
    var f = parseFloat(token);
    return isNaN(f) ? null : f;
  }

  function titleHasWide(title) {
    var t = String(title || "");
    return /\bWIDE\b/i.test(t) || /\b2E\b/i.test(t) || /\b4E\b/i.test(t) || /\b6E\b/i.test(t);
  }

  var WOMEN_SIZE_FLOOR = 5;

  function titleGender(title) {
    var t = String(title || "");
    if (/\b(PS|Preschool)\b/i.test(t)) return "td";
    if (/\b(TD|Toddler)\b/i.test(t)) return "td";
    if (/\b(GS|Grade School)\b/i.test(t) || /\bYouth\b/i.test(t)) return "gs";
    if (/\bWomen'?s?\b/i.test(t) || /\bWomens\b/i.test(t)) return "women";
    if (/\bMen'?s?\b/i.test(t) || /\bMens\b/i.test(t)) return "men";
    return null;
  }

  function tagBlob(tags) {
    if (Array.isArray(tags)) return tags.join(" ");
    return String(tags || "");
  }

  function isPreschoolProduct(ctx) {
    ctx = ctx || {};
    var blob = [ctx.title, ctx.type, ctx.handle, tagBlob(ctx.tags)].join(" ");
    return /\b(PS|Preschool)\b/i.test(blob);
  }

  function isLittleKidY(gender, sizeNumeric) {
    return gender === "gs" && sizeNumeric != null && sizeNumeric < 3.5;
  }

  function womenSizeAllowed(n) {
    return n != null && !isNaN(n) && n >= WOMEN_SIZE_FLOOR;
  }

  function localeFromSizeLocale(sl) {
    var v = String(sl || "").trim().toUpperCase();
    if (v === "EU") return "EU";
    if (v === "ASIA") return "ASIA";
    if (v === "US M" || v === "US W" || v === "US") return "US";
    return null;
  }

  function genderFromSizeLocale(sl) {
    var v = String(sl || "").trim().toUpperCase();
    if (v === "US M") return "men";
    if (v === "US W") return "women";
    return null;
  }

  function vendorKey(vendor) {
    return String(vendor || "").trim().toLowerCase();
  }

  function isBirkenstock(vendor) {
    return vendorKey(vendor) === "birkenstock";
  }

  function isSalomon(vendor) {
    return vendorKey(vendor) === "salomon";
  }

  function isNewBalance(vendor) {
    return vendorKey(vendor) === "new balance";
  }

  function buildLabel(o) {
    var widthNote = o.width === "wide" ? " Wide" : "";
    var n = o.sizeNumeric;
    if (isBirkenstock(o.vendor) || o.locale === "EU") {
      return "EU " + fmtNum(n);
    }
    if (o.gender === "gs") {
      var wsize = o.equivalent && o.equivalent.gender === "women" ? o.equivalent.size : null;
      if (wsize != null && womenSizeAllowed(wsize)) {
        return "GS " + fmtNum(n) + "Y / Women's " + fmtNum(wsize) + widthNote;
      }
      return "GS " + fmtNum(n) + "Y" + widthNote;
    }
    if (isSalomon(o.vendor)) {
      if (o.gender === "men") {
        var w = o.equivalent && o.equivalent.gender === "women" ? o.equivalent.size : n + 1;
        return "Men's " + fmtNum(n) + " / Women's " + fmtNum(w) + widthNote;
      }
      if (o.gender === "women") {
        var men = o.equivalent && o.equivalent.gender === "men" ? o.equivalent.size : n - 1;
        return "Men's " + fmtNum(men) + " / Women's " + fmtNum(n) + widthNote;
      }
    }
    if (isNewBalance(o.vendor) && o.gender === "men") {
      var w2 = o.equivalent && o.equivalent.gender === "women" ? o.equivalent.size : n + 1.5;
      return "Men's " + fmtNum(n) + " / Women's " + fmtNum(w2) + widthNote;
    }
    if (o.gender === "td") {
      return "TD " + fmtNum(n) + "C" + widthNote;
    }
    if (o.gender === "women") {
      return "Women's " + fmtNum(n) + widthNote;
    }
    if (o.gender === "men") {
      return "Men's " + fmtNum(n) + widthNote;
    }
    return fmtNum(n) + widthNote;
  }

  /**
   * Map one raw option value + product context → canonical row + hits.
   * @param {string} option raw variant option
   * @param {{title?:string,vendor?:string,sizeLocale?:string}} ctx
   */
  function mapOption(option, ctx) {
    ctx = ctx || {};
    var title = ctx.title || "";
    var vendor = ctx.vendor || "";
    var sizeLocale = ctx.sizeLocale || "";
    var raw = option == null ? "" : String(option);
    var s = norm(raw);
    var lower = s.toLowerCase();

    if (!s || isApparel(lower) || lower === "4-1") return null;

    var gender = null;
    var sizeNumeric = null;
    var width = "standard";
    var locale = localeFromSizeLocale(sizeLocale);
    var equivalent = null;
    var dualFromOption = false;
    var euFromOption = false;

    // §2.2 Youth / women dual
    var m = lower.match(/^(\d+(?:\.\d+)?)\s+(?:youth|y)\s*\/\s*(\d+(?:\.\d+)?)\s+women$/);
    if (m) {
      gender = "gs";
      sizeNumeric = parseFloat(m[1]);
      equivalent = { gender: "women", size: parseFloat(m[2]) };
      dualFromOption = true;
    }

    // §2.2 Youth / women slug  5-5-youth-7-women  |  4-youth-5-5-women
    if (sizeNumeric == null) {
      m = lower.match(/^(\d+(?:-5)?)-youth-(\d+(?:-5)?)-women$/);
      if (m) {
        gender = "gs";
        sizeNumeric = parseHalfJoin(m[1]);
        equivalent = { gender: "women", size: parseHalfJoin(m[2]) };
        dualFromOption = true;
      }
    }

    // §2.2 Men / women dual
    if (sizeNumeric == null) {
      m = lower.match(/^men\s+(\d+(?:\.\d+)?)\s*\/\s*women\s+(\d+(?:\.\d+)?)$/);
      if (m) {
        gender = "men";
        sizeNumeric = parseFloat(m[1]);
        equivalent = { gender: "women", size: parseFloat(m[2]) };
        dualFromOption = true;
      }
    }

    // §2.2 Compact MW — W between two numbers is Women, not width
    if (sizeNumeric == null) {
      m = s.match(/^M(\d+(?:\.\d+)?)W(\d+(?:\.\d+)?)$/i);
      if (m) {
        gender = "men";
        sizeNumeric = parseFloat(m[1]);
        equivalent = { gender: "women", size: parseFloat(m[2]) };
        dualFromOption = true;
      }
    }

    // §2.2 / §2.3 Men-only compact
    if (sizeNumeric == null) {
      m = s.match(/^M(\d+(?:\.\d+)?)$/i);
      if (m) {
        gender = "men";
        sizeNumeric = parseFloat(m[1]);
      }
    }

    // §2.2 Range + EU — canonical size is the EU integer
    if (sizeNumeric == null && /eu\s*\d+/.test(lower)) {
      var eu = lower.match(/eu\s*(\d+(?:\.\d+)?)/);
      if (eu) {
        locale = "EU";
        sizeNumeric = parseFloat(eu[1]);
        euFromOption = true;
        gender = null;
        equivalent = null;
      }
    }

    // §2.3 Suffix Y
    if (sizeNumeric == null) {
      m = s.match(/^(\d+(?:\.\d+)?)Y$/i);
      if (m) {
        gender = "gs";
        sizeNumeric = parseFloat(m[1]);
      }
    }

    // §2.3 Suffix C
    if (sizeNumeric == null) {
      m = s.match(/^(\d+(?:\.\d+)?)C$/i);
      if (m) {
        gender = "td";
        sizeNumeric = parseFloat(m[1]);
      }
    }

    // §2.3 Prefix C
    if (sizeNumeric == null) {
      m = s.match(/^C(\d+(?:\.\d+)?)$/i);
      if (m) {
        gender = "td";
        sizeNumeric = parseFloat(m[1]);
      }
    }

    // §2.3 Suffix W / w = WIDE, never women's
    if (sizeNumeric == null) {
      m = s.match(/^(\d+(?:\.\d+)?)[Ww]$/);
      if (m) {
        sizeNumeric = parseFloat(m[1]);
        width = "wide";
      }
    }

    // §2.4 hyphen half 9-5 == 9.5 (integer-hyphen-5, no other tokens)
    if (sizeNumeric == null) {
      m = lower.match(/^(\d+)-5$/);
      if (m) {
        sizeNumeric = parseInt(m[1], 10) + 0.5;
      } else if (/^\d+(?:\.\d+)?$/.test(lower)) {
        sizeNumeric = parseFloat(lower);
      }
    }

    if (sizeNumeric == null) return null;

    // §2.5 / §2.6 locale + Birkenstock EU force
    if (isBirkenstock(vendor) || euFromOption) {
      locale = "EU";
    } else if (!locale) {
      if (sizeNumeric >= 28) locale = "EU";
      else locale = "US";
    }

    if (locale === "ASIA") return null;

    if (locale === "EU") {
      gender = null;
      equivalent = null;
      dualFromOption = false;
    } else if (!gender) {
      gender = genderFromSizeLocale(sizeLocale);
      if (!gender) gender = titleGender(title);
    }

    // §2.6 title width
    if (titleHasWide(title)) width = "wide";

    // Preschool / C / 1Y–3Y never enter the women's filter, even if SizeLocale is US W.
    var preschool = isPreschoolProduct(ctx);
    if (preschool) {
      if (gender === "women" || !gender) gender = "td";
      if (equivalent && equivalent.gender === "women") {
        equivalent = null;
        dualFromOption = false;
      }
    }
    if (isLittleKidY(gender, sizeNumeric) && equivalent && equivalent.gender === "women") {
      equivalent = null;
      dualFromOption = false;
    }

    // Dual-index (skip if option already carried both sides, or PS / little-kid Y)
    if (locale !== "EU" && !equivalent && !preschool && !isLittleKidY(gender, sizeNumeric)) {
      if (isSalomon(vendor) && gender === "men") {
        equivalent = { gender: "women", size: sizeNumeric + 1 };
      } else if (isSalomon(vendor) && gender === "women") {
        equivalent = { gender: "men", size: sizeNumeric - 1 };
      } else if (isNewBalance(vendor) && gender === "men") {
        equivalent = { gender: "women", size: sizeNumeric + 1.5 };
      } else if (gender === "gs") {
        equivalent = { gender: "women", size: sizeNumeric + 1.5 };
      }
    }

    if (equivalent && equivalent.gender === "women" && !womenSizeAllowed(equivalent.size)) {
      equivalent = null;
    }

    var label = buildLabel({
      vendor: vendor,
      gender: gender,
      sizeNumeric: sizeNumeric,
      width: width,
      locale: locale,
      equivalent: equivalent
    });

    var hits = [];
    if (locale === "EU") {
      hits.push({ gender: null, size: sizeNumeric, width: width, locale: "EU" });
    } else if (gender) {
      if (!(gender === "women" && !womenSizeAllowed(sizeNumeric))) {
        hits.push({ gender: gender, size: sizeNumeric, width: width, locale: locale });
      }
      if (equivalent && !(equivalent.gender === "women" && !womenSizeAllowed(equivalent.size))) {
        hits.push({
          gender: equivalent.gender,
          size: equivalent.size,
          width: width,
          locale: locale
        });
      }
    }

    return {
      gender_bucket: gender,
      size_numeric: sizeNumeric,
      width: width,
      locale: locale,
      storefront_label: label,
      equivalent: equivalent,
      dual_from_option: dualFromOption,
      raw: raw,
      hits: hits
    };
  }

  function sizeOptionName(name) {
    return String(name || "").toLowerCase().indexOf("size") !== -1;
  }

  function applyPdpLabels(root) {
    root = root || document;
    var pickers = root.querySelectorAll("variant-picker");
    pickers.forEach(function (picker) {
      var ctxEl = picker.querySelector(".js-size-mapper-context");
      var ctx = {};
      if (ctxEl) {
        try { ctx = JSON.parse(ctxEl.textContent); } catch (e) { ctx = {}; }
      }
      picker.querySelectorAll(".option-selector").forEach(function (sel) {
        if (!sizeOptionName(sel.getAttribute("data-option"))) return;
        sel.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var raw = input.value;
          var mapped = mapOption(raw, ctx);
          var label = input.nextElementSibling;
          if (!label) return;
          var valueEl = label.querySelector(".js-value") || label;
          var shown = (mapped && mapped.storefront_label) ? mapped.storefront_label : raw;
          if (shown) {
            valueEl.textContent = shown;
            valueEl.setAttribute("data-raw-option", raw);
            label.classList.add("opt-label--computed-size");
          }
        });
        sel.querySelectorAll(".custom-select__option[data-value]").forEach(function (opt) {
          var raw = opt.getAttribute("data-value");
          if (raw == null || raw === "") return;
          var mapped = mapOption(raw, ctx);
          if (!mapped || !mapped.storefront_label) return;
          var text = opt.querySelector(".custom-select__option-text");
          if (text) text.textContent = mapped.storefront_label;
        });
      });
    });
  }

  function injectPdpStyles() {
    if (typeof document === "undefined") return;
    if (document.getElementById("midwood-size-label-css")) return;
    var style = document.createElement("style");
    style.id = "midwood-size-label-css";
    style.textContent = [
      ".opt-label--computed-size{",
      "min-width:4.5rem;height:auto;padding:0.55rem 0.65rem;white-space:normal;",
      "line-height:1.15;font-size:12px;letter-spacing:0.04em;text-align:center;",
      "}",
      ".opt-label--computed-size .js-value{display:block;white-space:normal;}"
    ].join("");
    document.head.appendChild(style);
  }

  if (typeof document !== "undefined") {
    injectPdpStyles();
    var start = function () { applyPdpLabels(document); };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
    document.addEventListener("shopify:section:load", function () { applyPdpLabels(document); });
  }

  return {
    mapOption: mapOption,
    fmtNum: fmtNum,
    applyPdpLabels: applyPdpLabels,
    sizeOptionName: sizeOptionName,
    isPreschoolProduct: isPreschoolProduct,
    womenSizeAllowed: womenSizeAllowed,
    WOMEN_SIZE_FLOOR: WOMEN_SIZE_FLOOR
  };
});
